import React, { useState, useEffect, useRef, useCallback } from 'react';
import './tmi.css';
import './MasterUsers.css';
import { connect, ConnectedProps } from 'react-redux';
import { mapStateToProps, mapDispatchToProps } from '../../redux/reduxActions';
import { UserInfo } from '../../models/models';
import {
    Grid,
    GridItemChangeEvent,
    GridCellProps,
    GridColumn,
} from '@progress/kendo-react-grid';
import { orderBy, SortDescriptor } from '@progress/kendo-data-query';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

type MasterUsersProps = PropsFromRedux & {
    menuItemId?: number;
    stateToLoad?: MasterUsersState;
    onSaveState?: (menuItemId: number, state: MasterUsersState) => void;
};

interface LookupListItem {
    value: string;
    display: string;
}

interface MasterUserRow {
    userID: number;
    userName: string;
    isSuperUser: boolean | null;
    isActive: boolean;
    firstName: string | null;
    lastName: string | null;
    defaultProjectId: number | null;
    passwordResetCode: string | null;
    passwordResetExpiry: string | null;
    emailAddress: string | null;
    externalEmailSendException: boolean;
    mfaCode: string | null;
    mfaExpiry: string | null;
    mfaMethod: 'None' | 'Email' | 'Authenticator' | null;
    authenticatorEnrolled: boolean;
    mfaChallengeIntervalDaysOverride: number | null;
    mfaCodeExpiryMinutesOverride: number | null;
    useSSO: boolean;
    usePWD: boolean;
    dateCreated: string | null;
}



interface MasterUsersState {
    data: MasterUserRow[];
    loading: boolean;
    errorMsg: string;
    sort: SortDescriptor[];
    filterTexts: { [key: string]: string };
    headerFocusField?: string | null;
    editItemId: number | null;
    originalItem: MasterUserRow | null;
    selectedIds: Set<number>;
    menuDataItem: MasterUserRow | null;
    menuAnchor: HTMLElement | null;
    menuAnchorRect: { left: number; top: number; right: number; bottom: number; width: number; height: number } | null;
    menuPosition: { left: number; top: number } | null;
    projectsDropdown: LookupListItem[];
    newMasterUserDlgOpen: boolean;
    newMasterUserDlgSaving: boolean;
    newMasterUserDlgError: string;
    newMasterUserDlgUsername: string;
    newMasterUserDlgFirstName: string;
    newMasterUserDlgLastName: string;
    newMasterUserDlgEmail: string;
    newMasterUserDlgIsSuperUser: boolean;
    newMasterUserDlgIsActive: boolean;
    newMasterUserDlgUseSSO: boolean;
    newMasterUserDlgUsePWD: boolean;
    newMasterUserDlgMfaMethod: 'None' | 'Email' | 'Authenticator';
    newMasterUserDlgDefaultProjectId: string;
    allowedProjectsDlgOpen: boolean;
    allowedProjectsUser: MasterUserRow | null;
    allowedProjectsSelected: Set<string>;
    allowedProjectsDlgLoading: boolean;
    allowedProjectsDlgError: string;
    deleteConfirmItem: MasterUserRow | null;
    deleteInProgress: boolean;
    deleteErrorMsg: string;
}

const DATA_ITEM_KEY = 'userID';
const EDIT_FIELD = 'inEdit';
const SELECTED_FIELD = 'selected';
const CANNOT_DEACTIVATE_SELF_ERROR = 'You cannot set your own user record as inactive.';
const AUTHENTICATION_METHOD_ERROR = 'An active user must have either Use SSO or Use Password enabled.';
const DUPLICATE_ACTIVE_EMAIL_ERROR = 'Email address must be unique among active users.';

const hasNoAuthenticationMethod = (isActive: boolean, useSSO: boolean, usePWD: boolean) =>
    isActive && !useSSO && !usePWD;

const hasDuplicateActiveEmail = (
    users: MasterUserRow[],
    emailAddress: string | null,
    isActive: boolean,
    excludeUserID?: number,
) => {
    const normalizedEmail = emailAddress?.trim().toLowerCase();
    return Boolean(normalizedEmail && isActive && users.some(user =>
        user.userID !== excludeUserID &&
        user.isActive &&
        user.emailAddress?.trim().toLowerCase() === normalizedEmail
    ));
};

const MasterUsers: React.FC<MasterUsersProps> = (props) => {
    const STORAGE_KEY = 'MasterUsers_state_v1';
    const MENU_FLIP_OFFSET = 8;
     const menuBlurTimeoutRef = useRef<any>(null);
    const menuRef = useRef<HTMLDivElement | null>(null);


    

    // attempt to restore saved UI state from sessionStorage
    const savedRaw = (() => {
        try { return sessionStorage.getItem(STORAGE_KEY); } catch { return null; }
    })();
    const saved = savedRaw ? JSON.parse(savedRaw) : null;

    // When navigating away to another menu item and back, the parent App restores
    // the full previous state (including filters) via stateToLoad/onSaveState, so
    // the page doesn't need to refilter/reselect. On first visit (or a full page
    // reload) there is no stateToLoad, so fall back to the sessionStorage-seeded defaults.
    const [state, setState] = useState<MasterUsersState>(props.stateToLoad ?? {
        data: [],
        loading: false,
        errorMsg: '',
        sort: saved?.sort ?? [{ field: 'userName', dir: 'asc' }],
        filterTexts: {},
        headerFocusField: null,
        editItemId: null,
        originalItem: null,
        selectedIds: new Set(saved?.selectedIds ?? []),
        menuDataItem: null,
        menuAnchor: null,
        menuAnchorRect: null,
        menuPosition: null,
        projectsDropdown: [],
        newMasterUserDlgOpen: false,
        newMasterUserDlgSaving: false,
        newMasterUserDlgError: '',
        newMasterUserDlgUsername: '',
        newMasterUserDlgFirstName: '',
        newMasterUserDlgLastName: '',
        newMasterUserDlgEmail: '',
        newMasterUserDlgUsePWD: true,
        newMasterUserDlgIsSuperUser: false,
        newMasterUserDlgIsActive: true,
        newMasterUserDlgUseSSO: false,
        newMasterUserDlgMfaMethod: 'Email',
        newMasterUserDlgDefaultProjectId: '',
        allowedProjectsDlgOpen: false,
        allowedProjectsUser: null,
        allowedProjectsSelected: new Set(),
        allowedProjectsDlgLoading: false,
        allowedProjectsDlgError: '',
        deleteConfirmItem: null,
        deleteInProgress: false,
        deleteErrorMsg: ''
    });

    // Ref to the latest state (kept in sync on every render below) so the unmount
    // cleanup effect, which only runs once on mount/unmount, can report the
    // up-to-date state rather than a stale snapshot captured on the initial render.
    const stateRef = useRef(state);
    stateRef.current = state;

    const saveState = useCallback(() => {
        try {
            const toSave = {
                sort: state.sort,
                selectedIds: Array.from(state.selectedIds ?? []),
            };
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
        } catch (e) {
            // ignore storage errors
        }
    }, [state.sort, state.selectedIds]);

    useEffect(() => {
        fetchData();
        fetchProjectsDropdown();
        return () => {
            // cleanup on unmount
            saveState();
            if (props.onSaveState && props.menuItemId !== undefined) {
                props.onSaveState(props.menuItemId, stateRef.current);
            }
            if (menuBlurTimeoutRef.current) clearTimeout(menuBlurTimeoutRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Close menu when clicking outside of it
    useEffect(() => {
        if (!state.menuDataItem) return;

        const handleClickOutside = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
                closeRowMenu();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [state.menuDataItem]);

    const fetchData = () => {
        setState(prev => ({ ...prev, loading: true, errorMsg: '' }));
        const url = props.userInf.currProject.apiUrl + '/api/TMI/GetMasterUsers';
        fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
        })
            .then(resp => resp.json())
            .then(res => {
                switch (res.callStatus) {
                    case 'OK':
                        setState(prev => ({ ...prev, data: res.results, loading: false }));
                        break;
                    case 'UNAUTH':
                        const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                        props.updateUserInfo(uInf);
                        break;
                    default:
                        setState(prev => ({ ...prev, errorMsg: 'Error fetching data: ' + res.callStatusMessage, loading: false }));
                }
            })
            .catch(err => {
                setState(prev => ({ ...prev, errorMsg: 'Error fetching data: ' + err.toString(), loading: false }));
            });
    }

    const fetchProjectsDropdown = () => {
        const url = props.userInf.currProject.apiUrl + '/api/TMI/GetProjectsForDropDown';
        fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'OK' && Array.isArray(res.results)) {
                    setState(prev => ({ ...prev, projectsDropdown: res.results }));
                }
            })
            .catch(err => {
                // Silently fail for dropdown data
                console.error('Error fetching projects dropdown:', err);
            });
    }




    const handleHeaderFocus = useCallback((field: string) => {
        setState(prev => ({ ...prev, headerFocusField: field }));
    }, []);

    const handleHeaderBlur = useCallback(() => {
        setState(prev => ({ ...prev, headerFocusField: null }));
    }, []);

    const toggleSort = useCallback((field: string) => {
        setState(prev => {
            const current = prev.sort && prev.sort.length > 0 ? prev.sort[0] : undefined;
            let newSort: SortDescriptor[] = [];
            if (!current || current.field !== field) {
                newSort = [{ field, dir: 'asc' }];
            } else if (current.dir === 'asc') {
                newSort = [{ field, dir: 'desc' }];
            } else {
                newSort = [];
            }
            return { ...prev, sort: newSort };
        });
    }, []);

    const handleHeaderSortClick = useCallback((field: string, e: React.MouseEvent<HTMLElement>) => {
        const target = e.target as HTMLElement;
        if (target.closest('input, select, textarea, button')) return;
        toggleSort(field);
    }, [toggleSort]);

    const handleHeaderFilterChange = useCallback((field: string, e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        setState(prev => ({
            ...prev,
            filterTexts: {
                ...prev.filterTexts,
                [field]: e.target.value
            }
        }));
    }, []);




    const handleEnterEdit = (dataItem: MasterUserRow) => {
        const original = state.data.find(d => d.userID === dataItem.userID);
        setState(prev => ({ ...prev, editItemId: dataItem.userID, originalItem: original ? { ...original } : null }));
    }

    const handleItemChange = (e: GridItemChangeEvent) => {
        const { field, value, dataItem } = e;
        if (!field || field === 'userID') return;
        setState(prev => ({ ...prev, data: prev.data.map(item => item.userID === dataItem.userID ? { ...item, [field]: value } : item) }));
    }

    const handleSave = (dataItem: MasterUserRow) => {
        if (dataItem.userID === props.userInf.userId && !dataItem.isActive) {
            setState(prev => ({ ...prev, errorMsg: CANNOT_DEACTIVATE_SELF_ERROR }));
            return;
        }

        if (hasNoAuthenticationMethod(dataItem.isActive, dataItem.useSSO, dataItem.usePWD)) {
            setState(prev => ({ ...prev, errorMsg: AUTHENTICATION_METHOD_ERROR }));
            return;
        }

        if (hasDuplicateActiveEmail(state.data, dataItem.emailAddress, dataItem.isActive, dataItem.userID)) {
            setState(prev => ({ ...prev, errorMsg: DUPLICATE_ACTIVE_EMAIL_ERROR }));
            return;
        }

        const url = props.userInf.currProject.apiUrl + '/api/TMI/AddOrUpdateMasterUser';
        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
            body: JSON.stringify(dataItem),
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                    props.updateUserInfo(uInf);
                } else if (res.callStatus !== 'OK') {
                    setState(prev => ({ ...prev, errorMsg: 'Error saving MasterUser: ' + res.callStatusMessage }));
                } else {
                    setState(prev => ({ ...prev, editItemId: null, originalItem: null, errorMsg: '' }));
                    fetchData();
                }
            })
            .catch(err => {
                setState(prev => ({ ...prev, errorMsg: 'Error saving MasterUser: ' + err.toString() }));
            });
    }

    const handleCancel = () => {
        const { originalItem } = state;
        setState(prev => ({ ...prev, data: originalItem ? prev.data.map(item => item.userID === originalItem.userID ? originalItem : item) : prev.data, editItemId: null, originalItem: null }));
    }


    const openDeleteConfirm = (dataItem: MasterUserRow) => {
        setState(prev => ({ ...prev, deleteConfirmItem: dataItem, deleteErrorMsg: '' }));
    }
    
        const closeDeleteConfirm = () => {
            setState(prev => ({ ...prev, deleteConfirmItem: null, deleteInProgress: false, deleteErrorMsg: '' }));
        }
        const confirmDelete = () => {
            const { deleteConfirmItem } = state;
            if (!deleteConfirmItem) return;
            setState(prev => ({ ...prev, deleteInProgress: true, deleteErrorMsg: '' }));
            const url = props.userInf.currProject.apiUrl + '/api/TMI/DeleteMasterUser';
            fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + props.userInf.token,
                },
                body: JSON.stringify(deleteConfirmItem.userID),
            })
                .then(resp => resp.json())
                .then(res => {
                    if (res.callStatus === 'UNAUTH') {
                        const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                        props.updateUserInfo(uInf);
                        closeDeleteConfirm();
                    } else if (res.callStatus !== 'OK') {
                        setState(prev => ({ ...prev, deleteInProgress: false, deleteErrorMsg: 'Delete failed: ' + res.callStatusMessage }));
                    } else {
                        closeDeleteConfirm();
                        fetchData();
                    }
                })
                .catch(err => {
                    setState(prev => ({ ...prev, deleteInProgress: false, deleteErrorMsg: 'Delete failed: ' + err.toString() }));
                });
        }
    


    const openRowMenu = (dataItem: MasterUserRow, anchor: HTMLElement) => {
        if (menuBlurTimeoutRef.current) clearTimeout(menuBlurTimeoutRef.current);
        // Debug: log anchor and bounding rect to help diagnose positioning issues
        // (debug logs removed)
        // We'll measure the real menu before showing to avoid a visual jump.
        // Snapshot anchor bounding rect now (stabilizes values for measurement) and
        // set menuPosition to null to indicate "hidden-for-measure" state.
        try {
            const ar = anchor.getBoundingClientRect();
            const anchorRect = { left: Math.round(ar.left), top: Math.round(ar.top), right: Math.round(ar.right), bottom: Math.round(ar.bottom), width: Math.round(ar.width), height: Math.round(ar.height) };
            setState(prev => ({ ...prev, menuDataItem: dataItem, menuAnchor: anchor, menuAnchorRect: anchorRect, menuPosition: null }));
        } catch {
            setState(prev => ({ ...prev, menuDataItem: dataItem, menuAnchor: anchor, menuAnchorRect: null, menuPosition: null }));
        }
        // After rendering, log the popup node and positioning to diagnose placement
        // removed debug logging
    }

    const closeRowMenu = () => {
        setState(prev => ({ ...prev, menuDataItem: null, menuAnchor: null }));
    }

    // useCallback (with no external deps, since setState's functional form is used) keeps this
    // handler's identity stable across renders so cell renderers that reference it don't get a
    // new function identity on every keystroke.
    const handleRowClick = useCallback((userID: number, e: React.MouseEvent) => {
        e.stopPropagation();
        setState(prev => {
            const newSelectedIds = new Set(prev.selectedIds);
            if (newSelectedIds.has(userID)) {
                newSelectedIds.delete(userID);
            } else {
                newSelectedIds.clear();
                newSelectedIds.add(userID);
            }
            return { ...prev, selectedIds: newSelectedIds };
        });
    }, []);

    const onMenuFocus = () => {
        if (menuBlurTimeoutRef.current) clearTimeout(menuBlurTimeoutRef.current);
    }

    const onMenuBlur = () => {
        menuBlurTimeoutRef.current = setTimeout(() => closeRowMenu(), 100);
    }

    // When menuAnchor exists but menuPosition is null, render the menu hidden
    // and measure its real size, then compute the final position and show it.
    useEffect(() => {
        if (!state.menuAnchor || state.menuPosition !== null) return;
        const el = menuRef.current;
        if (!el) return;
        const timer = setTimeout(() => {
            try {
                const menuRect = el.getBoundingClientRect();
                const snap = state.menuAnchorRect;
                const actualAnchorRect = snap ? snap : (state.menuAnchor ? state.menuAnchor.getBoundingClientRect() as any : { left: 0, right: 0, top: 0, bottom: 0, width: 0, height: 0 });
                const scrollX = window.scrollX || window.pageXOffset || 0;
                const scrollY = window.scrollY || window.pageYOffset || 0;
                const winW = window.innerWidth;
                const winH = window.innerHeight;
                const menuW = Math.round(menuRect.width);
                const menuH = Math.round(menuRect.height);

                let left = Math.round(actualAnchorRect.left + scrollX);
                let top = Math.round(actualAnchorRect.bottom + scrollY);

                // Clamp horizontally
                if (left + menuW > scrollX + winW) left = Math.round(scrollX + Math.max(8, winW - menuW - 8));
                if (left < scrollX + 8) left = Math.round(scrollX + 8);

                // If not enough room below, flip above and anchor bottom-left to the button
                if (top + menuH > scrollY + winH) {
                    // Account for the anchor (button) height when flipping above so the
                    // menu bottom aligns with the top of the button.
                    const flipped = Math.round(actualAnchorRect.top + scrollY - menuH + MENU_FLIP_OFFSET);
                    if (flipped >= scrollY + 8) top = flipped; else top = Math.round(scrollY + Math.max(8, winH - menuH - 8));
                    left = Math.round(actualAnchorRect.left + scrollX);
                    if (left + menuW > scrollX + winW) left = Math.round(scrollX + Math.max(8, winW - menuW - 8));
                    if (left < scrollX + 8) left = Math.round(scrollX + 8);
                }

                setState(prev => ({ ...prev, menuPosition: { left, top } }));
            } catch (e) {
                // ignore measurement errors
            }
        }, 10);
        return () => clearTimeout(timer);
    }, [state.menuAnchor, state.menuPosition, state.menuAnchorRect]);

    // Run measurement/flip when an anchor and a menuPosition exist (we may adjust based on real menu size)
    useEffect(() => {
        if (!state.menuAnchor || !state.menuPosition) return;
        const el = menuRef.current;
        if (!el) return;
        const timer = setTimeout(() => {
            try {
                const menuRect = el.getBoundingClientRect();
                const winW = window.innerWidth;
                const winH = window.innerHeight;
                const snap = state.menuAnchorRect;
                const anchorRect = snap ? snap as any : (state.menuAnchor ? state.menuAnchor.getBoundingClientRect() as any : { left: 0, right: 0, top: 0, bottom: 0, width: 0, height: 0 });
                const scrollX = window.scrollX || window.pageXOffset || 0;
                const scrollY = window.scrollY || window.pageYOffset || 0;
                const menuW = menuRect.width;
                const menuH = menuRect.height;

                const belowTop = Math.round(anchorRect.bottom + scrollY);
                const anchorLeftAbs = Math.round(anchorRect.left + scrollX);

                let newLeft = state.menuPosition!.left;
                let newTop = state.menuPosition!.top;

                // If currently positioned below the anchor (top at/after belowTop) but it overflows the viewport bottom, flip above
                if (state.menuPosition!.top >= belowTop - 2 && (menuRect.bottom > winH)) {
                    // Anchor bottom-left should align with menu bottom-left: left = anchor.left
                    newLeft = anchorLeftAbs;
                    // Compute flipped top so menu bottom aligns with the top of the button
                    // (account for the button height so menu doesn't render too low).
                    newTop = Math.round(anchorRect.top + scrollY - menuH + MENU_FLIP_OFFSET);
                    // Clamp horizontally if necessary
                    if (newLeft + menuW > scrollX + winW) newLeft = Math.round(scrollX + Math.max(8, winW - menuW - 8));
                    if (newLeft < scrollX + 8) newLeft = Math.round(scrollX + 8);
                } else {
                    // Otherwise, ensure menu is clamped within viewport horizontally/vertically
                    if (newLeft + menuW > scrollX + winW) newLeft = Math.round(scrollX + Math.max(8, winW - menuW - 8));
                    if (newLeft < scrollX + 8) newLeft = Math.round(scrollX + 8);
                    if (newTop + menuH > scrollY + winH) newTop = Math.round(scrollY + Math.max(8, winH - menuH - 8));
                    if (newTop < scrollY + 8) newTop = Math.round(scrollY + 8);
                }

                // Debugging info removed

                if (newLeft !== state.menuPosition!.left || newTop !== state.menuPosition!.top) {
                    setState(prev => ({ ...prev, menuPosition: { left: newLeft, top: newTop } }));
                }
            } catch (e) {
                // ignore measurement errors
            }
        }, 30);
        return () => clearTimeout(timer);
    }, [state.menuAnchor, state.menuPosition, state.menuAnchorRect]);

    useEffect(() => {
        if (!state.menuDataItem) return;

        const handleDocumentClick = (e: MouseEvent) => {
            const target = e.target as Node;
            // Check if click is outside the menu ref and outside the anchor button
            if (menuRef.current && !menuRef.current.contains(target) && 
                state.menuAnchor && !state.menuAnchor.contains(target)) {
                closeRowMenu();
            }
        };

        document.addEventListener('click', handleDocumentClick);
        return () => document.removeEventListener('click', handleDocumentClick);
    }, [state.menuDataItem, state.menuAnchor]);

    const openNewMasterUserDlg = () => {
        setState(prev => ({
            ...prev,
            newMasterUserDlgOpen: true,
            newMasterUserDlgSaving: false,
            newMasterUserDlgError: '',
            newMasterUserDlgUsername: '',
            newMasterUserDlgFirstName: '',
            newMasterUserDlgLastName: '',
            newMasterUserDlgEmail: '',
            newMasterUserDlgIsSuperUser: false,
            newMasterUserDlgIsActive: true,
            newMasterUserDlgUseSSO: false,
            newMasterUserDlgUsePWD: true,
            newMasterUserDlgMfaMethod: 'Email',
            newMasterUserDlgDefaultProjectId: '',
        }));
    }

    const closeNewMasterUserDlg = () => {
        setState(prev => ({ ...prev, newMasterUserDlgOpen: false }));
    }

    const resetUserAuthenticator = (user: MasterUserRow) => {
        closeRowMenu();
        const url = props.userInf.currProject.apiUrl + '/api/TMI/ResetUserAuthenticator';
        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
            body: JSON.stringify(user.userID),
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                    props.updateUserInfo(uInf);
                } else if (res.callStatus !== 'OK') {
                    setState(prev => ({ ...prev, errorMsg: 'Error resetting authenticator: ' + res.callStatusMessage }));
                } else {
                    fetchData();
                }
            })
            .catch(err => {
                setState(prev => ({ ...prev, errorMsg: 'Error resetting authenticator: ' + err.toString() }));
            });
    }

    const unlinkEntraIdentity = (user: MasterUserRow) => {
        closeRowMenu();
        const url = props.userInf.currProject.apiUrl + '/api/TMI/MasterUsers/UnlinkEntraIdentity';
        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
            body: JSON.stringify(user.userID),
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                    props.updateUserInfo(uInf);
                } else if (res.callStatus !== 'OK') {
                    setState(prev => ({ ...prev, errorMsg: 'Error unlinking Entra identity: ' + res.callStatusMessage }));
                } else {
                    fetchData();
                }
            })
            .catch(err => {
                setState(prev => ({ ...prev, errorMsg: 'Error unlinking Entra identity: ' + err.toString() }));
            });
    }

    const openAllowedProjectsDialog = (user: MasterUserRow) => {
        closeRowMenu();
        setState(prev => ({ ...prev, allowedProjectsDlgOpen: true, allowedProjectsUser: user, allowedProjectsDlgLoading: true, allowedProjectsDlgError: '' }));
        
        // Fetch user's allowed projects
        const url = `${props.userInf.currProject.apiUrl}/api/TMI/GetUserProjectsForDropDown?userId=${user.userID}`;
        fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'OK' && Array.isArray(res.results)) {
                    // results is an array of objects with value and display properties
                    const selectedValues = new Set((res.results as LookupListItem[]).map(item => item.value));
                    setState(prev => ({ ...prev, allowedProjectsSelected: selectedValues, allowedProjectsDlgLoading: false }));
                } else {
                    setState(prev => ({ ...prev, allowedProjectsDlgLoading: false, allowedProjectsDlgError: 'Error loading allowed projects' }));
                }
            })
            .catch(err => {
                setState(prev => ({ ...prev, allowedProjectsDlgLoading: false, allowedProjectsDlgError: 'Error loading allowed projects: ' + err.toString() }));
            });
    }

    const closeAllowedProjectsDialog = () => {
        setState(prev => ({ ...prev, allowedProjectsDlgOpen: false, allowedProjectsUser: null, allowedProjectsSelected: new Set() }));
    }

    const toggleProjectSelection = (projectValue: string) => {
        setState(prev => {
            const newSelected = new Set(prev.allowedProjectsSelected);
            if (newSelected.has(projectValue)) {
                newSelected.delete(projectValue);
            } else {
                newSelected.add(projectValue);
            }
            
            // Auto-save on toggle
            if (prev.allowedProjectsUser) {
                const url = props.userInf.currProject.apiUrl + '/api/TMI/UpdateUserAllowedProjects';
                fetch(url, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': 'Bearer ' + props.userInf.token,
                    },
                    body: JSON.stringify({
                        selected: prev.allowedProjectsUser.userID,
                        checked: Array.from(newSelected).map(id => parseInt(id, 10)),
                    }),
                })
                    .then(resp => resp.json())
                    .then(res => {
                        if (res.callStatus === 'UNAUTH') {
                            const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                            props.updateUserInfo(uInf);
                        } else if (res.callStatus !== 'OK') {
                            setState(s => ({
                                ...s,
                                allowedProjectsDlgError: 'Error saving: ' + res.callStatusMessage,
                            }));
                        }
                    })
                    .catch(err => {
                        setState(s => ({
                            ...s,
                            allowedProjectsDlgError: 'Error saving: ' + err.toString(),
                        }));
                    });
            }
            
            return { ...prev, allowedProjectsSelected: newSelected };
        });
    }



    const submitNewMasterUser = () => {
        const {
            newMasterUserDlgUsername,
            newMasterUserDlgFirstName,
            newMasterUserDlgLastName,
            newMasterUserDlgEmail,
            newMasterUserDlgIsSuperUser,
            newMasterUserDlgIsActive,
            newMasterUserDlgUseSSO,
            newMasterUserDlgUsePWD,
            newMasterUserDlgMfaMethod,
            newMasterUserDlgDefaultProjectId,
        } = state;

        if (hasNoAuthenticationMethod(newMasterUserDlgIsActive, newMasterUserDlgUseSSO, newMasterUserDlgUsePWD)) {
            setState(prev => ({ ...prev, newMasterUserDlgError: AUTHENTICATION_METHOD_ERROR }));
            return;
        }

        if (hasDuplicateActiveEmail(state.data, newMasterUserDlgEmail, newMasterUserDlgIsActive)) {
            setState(prev => ({ ...prev, newMasterUserDlgError: DUPLICATE_ACTIVE_EMAIL_ERROR }));
            return;
        }

        setState(prev => ({ ...prev, newMasterUserDlgSaving: true, newMasterUserDlgError: '' }));
        const url = props.userInf.currProject.apiUrl + '/api/TMI/AddOrUpdateMasterUser';
        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
            body: JSON.stringify({
                UserName: newMasterUserDlgUsername,
                FirstName: newMasterUserDlgFirstName,
                LastName: newMasterUserDlgLastName,
                EmailAddress: newMasterUserDlgEmail,
                IsSuperUser: newMasterUserDlgIsSuperUser,
                IsActive: newMasterUserDlgIsActive,
                UseSSO: newMasterUserDlgUseSSO,
                UsePWD: newMasterUserDlgUsePWD,
                MfaMethod: newMasterUserDlgMfaMethod,
                DefaultProjectId: newMasterUserDlgDefaultProjectId ? parseInt(newMasterUserDlgDefaultProjectId) : null,
            }),
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                    props.updateUserInfo(uInf);
                    setState(prev => ({ ...prev, newMasterUserDlgOpen: false, newMasterUserDlgSaving: false }));
                } else if (res.callStatus !== 'OK') {
                    setState(prev => ({ ...prev, newMasterUserDlgSaving: false, newMasterUserDlgError: 'Error creating MasterUser: ' + res.callStatusMessage }));
                } else {
                    setState(prev => ({ ...prev, newMasterUserDlgOpen: false, newMasterUserDlgSaving: false }));
                    fetchData();
                }
            })
            .catch(err => {
                setState(prev => ({ ...prev, newMasterUserDlgSaving: false, newMasterUserDlgError: 'Error creating MasterUser: ' + err.toString() }));
            });
    }

    // Shared editable text cell used by the Username/First Name/Last Name/Email columns.
    // It is memoized with useCallback so the GridColumn's `cell` prop keeps the same function
    // identity across renders. Previously each column defined its own inline arrow function in
    // JSX, which is recreated on every render (e.g. every keystroke); Kendo's Grid then saw a
    // "new" cell renderer and remounted the <input>, which dropped focus after a single character.
    const editableTextCell = useCallback((props: GridCellProps) => {
        const isSelected: boolean = props.dataItem[SELECTED_FIELD];
        const isEditing = props.dataItem[EDIT_FIELD] === true;
        const val = props.dataItem[props.field!] ?? '';
        if (isEditing) {
            return (
                <td
                    className="tmi-dialog-grid-text-cell"
                    onClick={(e) => handleRowClick(props.dataItem.userID, e)}
                    style={{ cursor: 'pointer', padding: '2px' }}
                >
                    <input
                        type="text"
                        value={val}
                        onMouseDown={e => e.stopPropagation()}
                        onClick={e => e.stopPropagation()}
                        onKeyDown={e => e.stopPropagation()}
                        onChange={e => setState(prev => ({ ...prev, data: prev.data.map(item => item.userID === props.dataItem.userID ? { ...item, [props.field!]: e.target.value } : item) }))}
                        style={{ width: '100%', border: '1px solid #999', borderRadius: '2px', padding: '4px', boxSizing: 'border-box' }}
                    />
                </td>
            );
        }
        return (
            <td
                className={`tmi-dialog-grid-text-cell ${isSelected ? 'tmi-dialog-grid-cell-selected' : ''}`}
                onClick={(e) => handleRowClick(props.dataItem.userID, e)}
                style={{ cursor: 'pointer' }}
                title={String(val)}
            >
                {val}
            </td>
        );
    }, [handleRowClick]);

    const IDCell = (props: GridCellProps) => {
        const val = props.dataItem[props.field!] ?? '';
        const inEdit: boolean = props.dataItem[EDIT_FIELD];
        const isSelected = state.selectedIds.has(props.dataItem.userID);
        return (
            <td
                className={`tmi-dialog-grid-center-cell ${isSelected && !inEdit ? 'tmi-dialog-grid-cell-selected' : ''}`}
                onClick={(e) => handleRowClick(props.dataItem.userID, e)}
                style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    backgroundColor: inEdit && !isSelected ? '#f5f5f5' : undefined,
                    color: inEdit && !isSelected ? '#999' : undefined,
                    fontStyle: inEdit && !isSelected ? 'italic' : 'normal',
                    cursor: 'pointer',
                }}
                title={String(val)}
            >
                {val}
            </td>
        );
    }

    const BooleanCell = (props: GridCellProps) => {
        const value: boolean = props.dataItem[props.field!] ?? false;
        const inEdit: boolean = props.dataItem[EDIT_FIELD];
        const isSelected = state.selectedIds.has(props.dataItem.userID);

        if (inEdit) {
            return (
                <td
                    className={`tmi-dialog-grid-center-cell ${isSelected && !inEdit ? 'tmi-dialog-grid-cell-selected' : ''}`}
                    onClick={(e) => handleRowClick(props.dataItem.userID, e)}
                    style={{ cursor: 'pointer' }}
                >
                    <input
                        type="checkbox"
                        defaultChecked={value}
                        onMouseDown={e => e.stopPropagation()}
                        onClick={e => e.stopPropagation()}
                        onKeyDown={e => e.stopPropagation()}
                        onChange={e => {
                            setState(prev => ({ ...prev, data: prev.data.map(item => item.userID === props.dataItem.userID ? { ...item, [props.field!]: e.target.checked } : item) }));
                        }}
                    />
                </td>
            );
        }

        return (
            <td
                className={`tmi-dialog-grid-center-cell ${isSelected ? 'tmi-dialog-grid-cell-selected' : ''}`}
                onClick={(e) => handleRowClick(props.dataItem.userID, e)}
                style={{ cursor: 'pointer' }}
            >
                {value ? '✓' : ''}
            </td>
        );
    }

    // Read-only indicator - authenticator enrollment can only change via the user completing
    // setup at login, or an admin resetting it from the row menu.
    const ReadOnlyBooleanCell = (props: GridCellProps) => {
        const value: boolean = props.dataItem[props.field!] ?? false;
        const isSelected = state.selectedIds.has(props.dataItem.userID);
        return (
            <td
                className={`tmi-dialog-grid-center-cell ${isSelected ? 'tmi-dialog-grid-cell-selected' : ''}`}
                onClick={(e) => handleRowClick(props.dataItem.userID, e)}
                style={{ cursor: 'pointer' }}
            >
                {value ? '✓' : ''}
            </td>
        );
    }

    const ProjectDropdownCell = (props: GridCellProps) => {
        const val = props.dataItem[props.field!] ?? '';
        const inEdit: boolean = props.dataItem[EDIT_FIELD];
        const isSelected = state.selectedIds.has(props.dataItem.userID);
        
        if (inEdit) {
            return (
                <td className={`tmi-dialog-grid-text-cell`} onClick={(e) => handleRowClick(props.dataItem.userID, e)} style={{ padding: '2px', cursor: 'pointer' }}>
                    <select
                        className="k-input k-input-md"
                        value={val}
                        onChange={e => {
                            const dataItem = props.dataItem;
                            const newVal = e.target.value ? parseInt(e.target.value, 10) : null;
                            setState(prev => ({ ...prev, data: prev.data.map(item => item.userID === dataItem.userID ? { ...item, [props.field!]: newVal } : item) }));
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                        style={{ width: '100%', border: '1px solid #999', borderRadius: '2px', boxSizing: 'border-box' }}
                    >
                        <option value="">-- Select --</option>
                        {state.projectsDropdown.map(proj => (
                            <option key={proj.value} value={proj.value}>
                                {proj.display}
                            </option>
                        ))}
                    </select>
                </td>
            );
        }

        // Display mode: show the project display name instead of just the ID
        const projDisplay = state.projectsDropdown.find(p => p.value === String(val))?.display || val;
        return (
            <td
                className={`tmi-dialog-grid-text-cell ${isSelected ? 'tmi-dialog-grid-cell-selected' : ''}`}
                onClick={(e) => handleRowClick(props.dataItem.userID, e)}
                style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer' }}
                title={String(projDisplay)}
            >
                {projDisplay}
            </td>
        );
    }

    const MfaMethodCell = (props: GridCellProps) => {
        const val = props.dataItem[props.field!] ?? 'Email';
        const inEdit: boolean = props.dataItem[EDIT_FIELD];
        const isSelected = state.selectedIds.has(props.dataItem.userID);

        if (inEdit) {
            return (
                <td className={`tmi-dialog-grid-text-cell`} onClick={(e) => handleRowClick(props.dataItem.userID, e)} style={{ padding: '2px', cursor: 'pointer' }}>
                    <select
                        className="k-input k-input-md"
                        value={val}
                        onChange={e => {
                            const dataItem = props.dataItem;
                            setState(prev => ({ ...prev, data: prev.data.map(item => item.userID === dataItem.userID ? { ...item, [props.field!]: e.target.value } : item) }));
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                        style={{ width: '100%', border: '1px solid #999', borderRadius: '2px', boxSizing: 'border-box' }}
                    >
                        <option value="None">None</option>
                        <option value="Email">Email</option>
                        <option value="Authenticator">Authenticator</option>
                    </select>
                </td>
            );
        }

        return (
            <td
                className={`tmi-dialog-grid-text-cell ${isSelected ? 'tmi-dialog-grid-cell-selected' : ''}`}
                onClick={(e) => handleRowClick(props.dataItem.userID, e)}
                style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer' }}
                title={String(val)}
            >
                {val === 'Authenticator' ? 'Authenticator' : val === 'None' ? 'None' : 'Email'}
            </td>
        );
    }

    // Nullable int override - blank means "use the global default" (see Master Projects)
    const NullableNumberCell = (props: GridCellProps) => {
        const val = props.dataItem[props.field!];
        const inEdit: boolean = props.dataItem[EDIT_FIELD];
        const isSelected = state.selectedIds.has(props.dataItem.userID);

        if (inEdit) {
            return (
                <td className={`tmi-dialog-grid-text-cell`} onClick={(e) => handleRowClick(props.dataItem.userID, e)} style={{ cursor: 'pointer', padding: '2px' }}>
                    <input
                        type="number"
                        value={val ?? ''}
                        placeholder="Default"
                        onMouseDown={e => e.stopPropagation()}
                        onClick={e => e.stopPropagation()}
                        onKeyDown={e => e.stopPropagation()}
                        onChange={e => {
                            const dataItem = props.dataItem;
                            const newVal = e.target.value === '' ? null : parseInt(e.target.value, 10);
                            setState(prev => ({ ...prev, data: prev.data.map(item => item.userID === dataItem.userID ? { ...item, [props.field!]: newVal } : item) }));
                        }}
                        style={{ width: '100%', border: '1px solid #999', borderRadius: '2px', padding: '4px', boxSizing: 'border-box' }}
                    />
                </td>
            );
        }

        return (
            <td
                className={`tmi-dialog-grid-text-cell ${isSelected ? 'tmi-dialog-grid-cell-selected' : ''}`}
                onClick={(e) => handleRowClick(props.dataItem.userID, e)}
                style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer' }}
                title={val ?? 'Default'}
            >
                {val ?? <span style={{ color: '#999', fontStyle: 'italic' }}>Default</span>}
            </td>
        );
    }

    // Header renderers ref for caching (stateRef is declared earlier, near the state hook)
    const headerCellRenderersRef = useRef<{ [key: string]: (props: any) => JSX.Element }>({});
    const HEADER_RENDERER_VERSION = 'v2';

    const buildHeaderCellRenderer = (field: string, title: string) => {
        const cacheKey = `${HEADER_RENDERER_VERSION}_text_${field}`;
        if (!headerCellRenderersRef.current[cacheKey]) {
            headerCellRenderersRef.current[cacheKey] = (props: any) => {
                const val = stateRef.current.filterTexts?.[field] ?? '';
                const focused = stateRef.current.headerFocusField === field;
                const sortDir = (stateRef.current.sort && stateRef.current.sort.length > 0 && stateRef.current.sort[0].field === field) ? (stateRef.current.sort[0].dir as string) : undefined;
                return (
                    <th className={`tmi-header-filter ${sortDir === 'asc' ? 'sort-asc' : sortDir === 'desc' ? 'sort-desc' : 'sort-none'}`} onClick={(e) => handleHeaderSortClick(field, e)}>
                        <div className="k-link" onClick={() => toggleSort(field)} aria-hidden="true" />
                        <div className={`tmi-floating-wrapper ${val ? 'has-value' : ''} ${focused ? 'focused' : ''}`}>
                            <label onClick={() => toggleSort(field)} style={{ cursor: 'pointer', pointerEvents: 'auto', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <span>{title}</span>
                                <span className="tmi-sort-glyph-inline">{sortDir === 'asc' ? '▲' : sortDir === 'desc' ? '▼' : '↕'}</span>
                            </label>
                            <input type="text" className="k-input k-input-sm" value={val} onChange={(e) => handleHeaderFilterChange(field, e)} onFocus={() => handleHeaderFocus(field)} onBlur={() => handleHeaderBlur()} onClick={(e) => { e.stopPropagation(); }} onMouseDown={(e) => { e.stopPropagation(); }} />
                        </div>
                        <div className="tmi-header-sort-zone" onClick={() => toggleSort(field)} />
                    </th>
                );
            };
        }
        return headerCellRenderersRef.current[cacheKey];
    };

    const buildBooleanHeaderCellRenderer = (field: string, title: string) => {
        const cacheKey = `${HEADER_RENDERER_VERSION}_bool_${field}`;
        if (!headerCellRenderersRef.current[cacheKey]) {
            headerCellRenderersRef.current[cacheKey] = (props: any) => {
                const val = stateRef.current.filterTexts?.[field] ?? '';
                const focused = stateRef.current.headerFocusField === field;
                const sortDir = (stateRef.current.sort && stateRef.current.sort.length > 0 && stateRef.current.sort[0].field === field) ? (stateRef.current.sort[0].dir as string) : undefined;
                return (
                    <th className={`tmi-header-filter ${sortDir === 'asc' ? 'sort-asc' : sortDir === 'desc' ? 'sort-desc' : 'sort-none'}`} onClick={(e) => handleHeaderSortClick(field, e)}>
                        <div className="k-link" onClick={() => toggleSort(field)} aria-hidden="true" />
                        <div className={`tmi-select-floating ${val ? 'has-value' : ''} ${focused ? 'focused' : ''}`}>
                            <label onClick={() => toggleSort(field)} style={{ cursor: 'pointer', pointerEvents: 'auto', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <span>{title}</span>
                                <span className="tmi-sort-glyph-inline">{sortDir === 'asc' ? '▲' : sortDir === 'desc' ? '▼' : '↕'}</span>
                            </label>
                            <select className="k-input k-input-sm" value={val} onChange={(e) => handleHeaderFilterChange(field, e as unknown as React.ChangeEvent<HTMLInputElement>)} onFocus={() => handleHeaderFocus(field)} onBlur={() => handleHeaderBlur()} onClick={(e) => { e.stopPropagation(); }} onMouseDown={(e) => { e.stopPropagation(); }}>
                                <option value="">All</option>
                                <option value="true">Yes</option>
                                <option value="false">No</option>
                            </select>
                        </div>
                        <div className="tmi-header-sort-zone" onClick={() => toggleSort(field)} />
                    </th>
                );
            };
        }
        return headerCellRenderersRef.current[cacheKey];
    };

    const createSimpleHeaderCell = (field: string, title: string) => {
        const cacheKey = `${HEADER_RENDERER_VERSION}_simple_${field}`;
        if (!headerCellRenderersRef.current[cacheKey]) {
            headerCellRenderersRef.current[cacheKey] = (props: any) => {
                const sortDir = (stateRef.current.sort && stateRef.current.sort.length > 0 && stateRef.current.sort[0].field === field) ? (stateRef.current.sort[0].dir as string) : undefined;
                const onSimpleHeaderMouseDown = (e: React.MouseEvent<HTMLElement>) => {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleSort(field);
                };
                const blockSimpleHeaderClick = (e: React.MouseEvent<HTMLElement>) => {
                    e.preventDefault();
                    e.stopPropagation();
                };
                return (
                    <th className="tmi-header-filter">
                        <div className="k-link" onMouseDown={onSimpleHeaderMouseDown} onClick={blockSimpleHeaderClick} aria-hidden="true" />
                        <div onMouseDown={onSimpleHeaderMouseDown} onClick={blockSimpleHeaderClick} style={{ 
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '4px',
                            padding: '4px 6px',
                            boxSizing: 'border-box',
                            fontSize: '12px',
                            lineHeight: '1.3',
                            whiteSpace: 'normal',
                            wordWrap: 'break-word',
                            flex: 1,
                            cursor: 'pointer',
                            userSelect: 'none'
                        }}>
                            <span onMouseDown={onSimpleHeaderMouseDown} onClick={blockSimpleHeaderClick}>{title}</span>
                            <span className="tmi-sort-glyph-inline" onMouseDown={onSimpleHeaderMouseDown} onClick={blockSimpleHeaderClick}>{sortDir === 'asc' ? '▲' : sortDir === 'desc' ? '▼' : '↕'}</span>
                        </div>
                    </th>
                );
            };
        }
        return headerCellRenderersRef.current[cacheKey];
    };

    const HamburgerHeaderCell = () => {
        return (
            <th className="tmi-header-action-cell tmi-command-column">
                <button className="k-button k-button-sm k-rounded-md tmi-header-action-button" onClick={fetchData} disabled={loading} title="Refresh">
                    <span className="k-icon k-font-icon k-i-reload" />
                </button>
            </th>
        );
    }

    const CommandHeaderCell = () => {
        return (
            <th className="tmi-command-header-cell tmi-command-column">
                <button
                    className="k-button k-button-sm k-rounded-md k-button-solid-primary"
                    title="New Master User"
                    onClick={openNewMasterUserDlg}
                >
                    <span className="k-icon k-font-icon k-i-plus" />
                </button>
            </th>
        );
    }

    const CommandCell = (props: GridCellProps) => {
        const { dataItem } = props;
        const inEdit: boolean = dataItem[EDIT_FIELD];
        // Simple inline command buttons (no overlay)
        return (
            <td className="tmi-command-column tmi-command-cell">
                {inEdit ? (
                    <>
                        <button className="k-button k-button-sm k-rounded-md k-button-solid-secondary tmi-dialog-action-button tmi-dialog-action-button-first" onClick={(e) => { handleRowClick(dataItem.userID, e); handleCancel(); }} title="Cancel">
                            <span className="k-icon k-font-icon k-i-cancel" />
                        </button>
                        <button className="k-button k-button-sm k-rounded-md k-button-solid-error tmi-dialog-action-button tmi-dialog-action-button-next" onClick={(e) => { handleRowClick(dataItem.userID, e); openDeleteConfirm(dataItem as MasterUserRow); }} title="Delete">
                            <span className="k-icon k-font-icon k-i-trash" />
                        </button>
                    </>
                ) : (
                    <>
                        <button className="k-button k-button-sm k-rounded-md k-button-solid-base tmi-dialog-action-button tmi-dialog-action-button-first" onClick={(e) => { handleRowClick(dataItem.userID, e); handleEnterEdit(dataItem); }} title="Edit">
                            <span className="k-icon k-font-icon k-i-edit" />
                        </button>
                        <button className="k-button k-button-sm k-rounded-md k-button-solid-error tmi-dialog-action-button tmi-dialog-action-button-next" onClick={(e) => { handleRowClick(dataItem.userID, e); openDeleteConfirm(dataItem as MasterUserRow); }} title="Delete">
                            <span className="k-icon k-font-icon k-i-trash" />
                        </button>
                    </>
                )}
            </td>
        );
    }

    const HamburgerCell = (props: GridCellProps) => {
        const { dataItem } = props;
        const inEdit: boolean = dataItem[EDIT_FIELD];
        return (
            <td style={{ textAlign: 'center', padding: '2px' }}>
                {inEdit ? (
                    <button
                        className="k-button k-button-sm k-rounded-md k-button-solid-success"
                        title="Save"
                        onClick={(e) => { handleRowClick(dataItem.userID, e); handleSave(dataItem); }}
                    >
                        <span className="k-icon k-font-icon k-i-check" />
                    </button>
                ) : (
                    <button
                        className="k-button k-button-sm k-rounded-md"
                        title="Actions"
                        onClick={e => {
                            e.stopPropagation();
                            openRowMenu(dataItem as MasterUserRow, e.currentTarget);
                        }}
                    >
                        <span className="k-icon k-font-icon k-i-more-vertical" />
                    </button>
                )}
            </td>
        );
    }

    const {
        data,
        loading,
        errorMsg,
        sort,
        filterTexts,
        editItemId,
        selectedIds,
    } = state;

    // Manual filtering logic
    const filteredData = Object.keys(filterTexts).length === 0 || Object.values(filterTexts).every(v => !v)
        ? data
        : data.filter(item => {
            return Object.entries(filterTexts).every(([f, q]) => {
                if (!q) return true;
                const val = (item as any)[f];
                const ql = q.trim().toLowerCase();
                if (ql === 'true' || ql === 'false') {
                    return String(val ?? '').toLowerCase() === ql;
                }
                return String(val ?? '').toLowerCase().includes(ql);
            });
        });

    const sortedData = orderBy(filteredData, sort);
    
    const gridData = sortedData.map(item => ({
        ...item,
        [EDIT_FIELD]: item.userID === editItemId,
        [SELECTED_FIELD]: selectedIds.has(item.userID),
    }));

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', flex: 1 }}>
            <div style={{ display: 'flex', flexDirection: 'column', marginBottom: '8px', gap: '6px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {loading && <span className="k-icon k-font-icon k-i-loading" />}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                </div>
            </div>
            {errorMsg && <div style={{ color: 'red', marginBottom: '8px' }}>{errorMsg}</div>}
            <Grid
                className="tmi-grid MasterUsers-grid"
                style={{ flex: 1, minHeight: 0 }}
                data={gridData}
                dataItemKey={DATA_ITEM_KEY}
                resizable
                editField={EDIT_FIELD}
                onItemChange={handleItemChange}
            >
                <GridColumn headerCell={HamburgerHeaderCell} cell={HamburgerCell} width={40} filterable={false} sortable={false} headerClassName="wrap-header" />
                <GridColumn headerCell={createSimpleHeaderCell('userID', 'ID')} cell={IDCell} field='userID' title='ID' filterable={false} sortable width={60} headerClassName="wrap-header" resizable={true} />
                <GridColumn headerCell={buildHeaderCellRenderer('userName', 'Username')} field='userName' title='Username' filterable={false} sortable cell={editableTextCell} headerClassName="wrap-header" resizable={true} />
                <GridColumn headerCell={buildHeaderCellRenderer('firstName', 'First Name')} field='firstName' title='First Name' filterable={false} sortable cell={editableTextCell} headerClassName="wrap-header" resizable={true} />
                <GridColumn headerCell={buildHeaderCellRenderer('lastName', 'Last Name')} field='lastName' title='Last Name' filterable={false} sortable cell={editableTextCell} headerClassName="wrap-header" resizable={true} />
                <GridColumn headerCell={buildHeaderCellRenderer('emailAddress', 'Email')} field='emailAddress' title='Email' filterable={false} sortable cell={editableTextCell} headerClassName="wrap-header" resizable={true} />
                <GridColumn headerCell={buildBooleanHeaderCellRenderer('isActive', 'Active')} field='isActive' title='Active' cell={BooleanCell} filterable={false} sortable headerClassName="wrap-header" resizable={true} />
                <GridColumn headerCell={buildBooleanHeaderCellRenderer('isSuperUser', 'Super User')} field='isSuperUser' title='Super User' cell={BooleanCell} filterable={false} sortable headerClassName="wrap-header" resizable={true} />
                <GridColumn headerCell={buildBooleanHeaderCellRenderer('useSSO', 'Use SSO')} field='useSSO' title='Use SSO' cell={BooleanCell} filterable={false} sortable headerClassName="wrap-header" resizable={true} />
                <GridColumn headerCell={buildBooleanHeaderCellRenderer('usePWD', 'Use Password')} field='usePWD' title='Use Password' cell={BooleanCell} filterable={false} sortable headerClassName="wrap-header" resizable={true} />
                <GridColumn headerCell={buildHeaderCellRenderer('mfaMethod', 'MFA Method')} field='mfaMethod' title='MFA Method' cell={MfaMethodCell} filterable={false} sortable headerClassName="wrap-header" resizable={true} />
                <GridColumn headerCell={buildBooleanHeaderCellRenderer('authenticatorEnrolled', 'Authenticator Enrolled')} field='authenticatorEnrolled' title='Authenticator Enrolled' cell={ReadOnlyBooleanCell} filterable={false} sortable headerClassName="wrap-header" resizable={true} />
                <GridColumn headerCell={buildHeaderCellRenderer('mfaChallengeIntervalDaysOverride', 'MFA Interval Override (Days)')} field='mfaChallengeIntervalDaysOverride' title='MFA Interval Override (Days)' cell={NullableNumberCell} filterable={false} sortable headerClassName="wrap-header" resizable={true} />
                <GridColumn headerCell={buildHeaderCellRenderer('mfaCodeExpiryMinutesOverride', 'MFA Code Expiry Override (Mins)')} field='mfaCodeExpiryMinutesOverride' title='MFA Code Expiry Override (Mins)' cell={NullableNumberCell} filterable={false} sortable headerClassName="wrap-header" resizable={true} />
                <GridColumn headerCell={buildHeaderCellRenderer('defaultProjectId', 'Default Project')} cell={ProjectDropdownCell} field='defaultProjectId' title='Default Project ID' filterable={false} sortable headerClassName="wrap-header" resizable={true} />         
                <GridColumn headerCell={CommandHeaderCell} cell={CommandCell} width={80} filterable={false} sortable={false} locked={true} resizable={false} headerClassName="wrap-header tmi-command-column" />
            </Grid>

            {state.menuDataItem && (
                <div
                    ref={menuRef}
                    onFocus={onMenuFocus}
                    onBlur={onMenuBlur}
                    style={{
                        position: 'fixed',
                        left: state.menuPosition ? `${state.menuPosition.left}px` : '-9999px',
                        top: state.menuPosition ? `${state.menuPosition.top}px` : '-9999px',
                        backgroundColor: '#fff',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        zIndex: 1000,
                        minWidth: '200px',
                    }}
                >
                    <div
                        style={{
                            padding: '6px 12px',
                            fontSize: '14px',
                            cursor: 'pointer',
                        }}
                        onClick={() => openAllowedProjectsDialog(state.menuDataItem as MasterUserRow)}
                        onMouseDown={e => e.preventDefault()}
                        onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                        onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                    >
                        Allowed Projects
                    </div>
                    {(state.menuDataItem?.mfaMethod === 'Authenticator' || state.menuDataItem?.authenticatorEnrolled) &&
                        <div
                            style={{
                                padding: '6px 12px',
                                fontSize: '14px',
                                cursor: 'pointer',
                            }}
                            onClick={() => resetUserAuthenticator(state.menuDataItem as MasterUserRow)}
                            onMouseDown={e => e.preventDefault()}
                            onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                            onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                        >
                            Reset Authenticator Enrollment
                        </div>
                    }
                    {state.menuDataItem?.useSSO &&
                        <div
                            style={{
                                padding: '6px 12px',
                                fontSize: '14px',
                                cursor: 'pointer',
                            }}
                            onClick={() => unlinkEntraIdentity(state.menuDataItem as MasterUserRow)}
                            onMouseDown={e => e.preventDefault()}
                            onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                            onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                        >
                            Unlink Entra Identity
                        </div>
                    }
                </div>
            )}

            {state.deleteConfirmItem && (
                <Dialog
                    title="Confirm Delete"
                    onClose={closeDeleteConfirm}
                    width={420}
                >
                    <p style={{ padding: '8px 0' }}>
                        Are you sure you want to delete <strong>{state.deleteConfirmItem?.userName}</strong>?
                    </p>
                    {state.deleteErrorMsg && (
                        <div style={{ color: 'red', marginBottom: '8px' }}>{state.deleteErrorMsg}</div>
                    )}
                    <DialogActionsBar>
                        <button className="k-button k-button-md k-rounded-md" onClick={closeDeleteConfirm} disabled={state.deleteInProgress}>Cancel</button>
                        <button
                            className="k-button k-button-md k-rounded-md k-button-solid-error"
                            onClick={confirmDelete}
                            disabled={state.deleteInProgress}
                        >
                            {state.deleteInProgress ? <span className="k-icon k-font-icon k-i-loading" /> : 'Delete'}
                        </button>
                    </DialogActionsBar>
                </Dialog>
            )}
            {state.newMasterUserDlgOpen && (
                <Dialog
                    title="New MasterUser"
                    onClose={closeNewMasterUserDlg}
                    width={480}
                >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '8px 0', maxHeight: '500px', overflowY: 'auto' }}>
                        {state.newMasterUserDlgError && (
                            <div style={{ color: 'red' }}>{state.newMasterUserDlgError}</div>
                        )}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <label>Username</label>
                            <input
                                className="k-input k-input-md k-rounded-md"
                                value={state.newMasterUserDlgUsername}
                                onChange={e => setState(prev => ({ ...prev, newMasterUserDlgUsername: e.target.value }))}
                                style={{ width: '100%' }}
                            />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <label>First Name</label>
                            <input
                                className="k-input k-input-md k-rounded-md"
                                value={state.newMasterUserDlgFirstName}
                                onChange={e => setState(prev => ({ ...prev, newMasterUserDlgFirstName: e.target.value }))}
                                style={{ width: '100%' }}
                            />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <label>Last Name</label>
                            <input
                                className="k-input k-input-md k-rounded-md"
                                value={state.newMasterUserDlgLastName}
                                onChange={e => setState(prev => ({ ...prev, newMasterUserDlgLastName: e.target.value }))}
                                style={{ width: '100%' }}
                            />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <label>Email Address</label>
                            <input
                                className="k-input k-input-md k-rounded-md"
                                value={state.newMasterUserDlgEmail}
                                onChange={e => setState(prev => ({ ...prev, newMasterUserDlgEmail: e.target.value }))}
                                style={{ width: '100%' }}
                            />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <label>Default Project ID</label>
                            <select
                                className="k-input k-input-md k-rounded-md"
                                value={state.newMasterUserDlgDefaultProjectId}
                                onChange={e => setState(prev => ({ ...prev, newMasterUserDlgDefaultProjectId: e.target.value }))}
                                style={{ width: '100%' }}
                            >
                                <option value="">-- Select a Project --</option>
                                {state.projectsDropdown.map(proj => (
                                    <option key={proj.value} value={proj.value}>
                                        {proj.display}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', borderTop: '1px solid #e0e0e0', paddingTop: '8px' }}>
                            <label style={{ fontWeight: 'bold' }}>Options</label>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <input
                                    type="checkbox"
                                    checked={state.newMasterUserDlgIsActive}
                                    onChange={e => setState(prev => ({ ...prev, newMasterUserDlgIsActive: e.target.checked }))}
                                />
                                <label>Active</label>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <input
                                    type="checkbox"
                                    checked={state.newMasterUserDlgIsSuperUser}
                                    onChange={e => setState(prev => ({ ...prev, newMasterUserDlgIsSuperUser: e.target.checked }))}
                                />
                                <label>Super User</label>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <input
                                    type="checkbox"
                                    checked={state.newMasterUserDlgUseSSO}
                                    onChange={e => setState(prev => ({ ...prev, newMasterUserDlgUseSSO: e.target.checked }))}
                                />
                                <label>Use SSO</label>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <input
                                    type="checkbox"
                                    checked={state.newMasterUserDlgUsePWD}
                                    onChange={e => setState(prev => ({ ...prev, newMasterUserDlgUsePWD: e.target.checked }))}
                                />
                                <label>Use Password</label>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <label style={{ minWidth: '100px' }}>MFA Method</label>
                                <select
                                    className="k-input k-input-md"
                                    value={state.newMasterUserDlgMfaMethod}
                                    onChange={e => setState(prev => ({ ...prev, newMasterUserDlgMfaMethod: e.target.value as 'None' | 'Email' | 'Authenticator' }))}
                                >
                                    <option value="None">None</option>
                                    <option value="Email">Email</option>
                                    <option value="Authenticator">Authenticator</option>
                                </select>
                            </div>
                        </div>
                    </div>
                    <DialogActionsBar>
                        <button className="k-button k-button-md k-rounded-md" onClick={closeNewMasterUserDlg} disabled={state.newMasterUserDlgSaving}>Cancel</button>
                        <button
                            className="k-button k-button-md k-rounded-md k-button-solid-primary"
                            onClick={submitNewMasterUser}
                            disabled={state.newMasterUserDlgSaving}
                        >
                            {state.newMasterUserDlgSaving ? <span className="k-icon k-font-icon k-i-loading" /> : 'Create'}
                        </button>
                    </DialogActionsBar>
                </Dialog>
            )}

            {state.allowedProjectsDlgOpen && (
                <Dialog
                    title={`Allowed Projects for ${state.allowedProjectsUser?.userName}`}
                    onClose={closeAllowedProjectsDialog}
                    width={480}
                >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '8px 0' }}>
                        <div className="tmi-dialog-list-container">
                            {state.allowedProjectsDlgError && (
                                <div className="tmi-dialog-list-error">{state.allowedProjectsDlgError}</div>
                            )}
                            {state.allowedProjectsDlgLoading ? (
                                <div className="tmi-dialog-list-loading">
                                    <span className="k-icon k-font-icon k-i-loading" />
                                    Loading projects...
                                </div>
                            ) : (
                                <div className="tmi-dialog-list-items">
                                    {state.projectsDropdown.length === 0 ? (
                                        <div className="tmi-dialog-list-empty">No projects available</div>
                                    ) : (
                                        state.projectsDropdown.map(proj => (
                                            <div key={proj.value} className="tmi-dialog-list-item">
                                                <input
                                                    type="checkbox"
                                                    checked={state.allowedProjectsSelected.has(proj.value)}
                                                    onChange={() => toggleProjectSelection(proj.value)}
                                                    id={`project-${proj.value}`}
                                                />
                                                <label htmlFor={`project-${proj.value}`}>{proj.display}</label>
                                            </div>
                                        ))
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                    <DialogActionsBar>
                        <button className="k-button k-button-md k-rounded-md" onClick={closeAllowedProjectsDialog}>
                            Close
                        </button>
                    </DialogActionsBar>
                </Dialog>
            )}
        </div>
    );
}

export default connector(MasterUsers);
